import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import api from '../../../utils/api';

const ViewSChallan = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const superAdminId = user?.id;
  const navigate = useNavigate();

  const [challans, setChallans] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);

  const fetchChallans = async () => {
    try {
      const res = await api.get(`/api/schallans/get-all/${superAdminId}`);
      setChallans(res.data);
    } catch (err) {
      console.error('Error fetching challans:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this challan?')) {
      try {
        await api.delete(`/api/schallans/delete/${id}`);
        fetchChallans(); // Refresh the list
      } catch (err) {
        console.error('Error deleting challan:', err);
      }
    }
  };

  const generatePDF = async (challan) => {
    try {
      const existingPdfBytes = await fetch('/challan-template.pdf').then(
        (res) => res.arrayBuffer()
      );
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const page = pages[0];

      const drawSafeText = (text, opts) => {
        try {
          if (typeof text === 'string' || typeof text === 'number') {
            page.drawText(String(text), opts);
          }
        } catch (e) {
          console.warn('⚠️ drawText failed:', text);
        }
      };

      // Order No beside the label
      drawSafeText(`${challan.challanNumber ?? 'N/A'}`, {
        x: 460,
        y: 670,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });

      // Date a little lower
      drawSafeText(`${challan.date?.slice(0, 10) ?? 'N/A'}`, {
        x: 450,
        y: 700,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });

      // Customer name a little lower
      drawSafeText(`${challan.customer ?? 'N/A'}`, {
        x: 160,
        y: 675,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });

      // Items
      let startY = 592;
      const rowHeight = 20;

      if (Array.isArray(challan.items) && challan.items.length > 0) {
        // Rows
        challan.items.slice(0, 15).forEach((item, idx) => {
          const y = startY - rowHeight * (idx + 1);
          const product = item?.product ?? 'N/A';
          const quantity = item?.quantity ?? 0;
          const unit = item?.unit ?? '';
          const price = Number(item?.price ?? 0);
          const total = quantity * price;

          drawSafeText(product, { x: 112, y, size: 10, font });
          drawSafeText(String(quantity), { x: 335, y, size: 10, font });
          drawSafeText(unit, { x: 380, y, size: 10, font });
          drawSafeText(`${price.toFixed(2)}`, {
            x: 418,
            y,
            size: 10,
            font,
          });
          drawSafeText(`${total.toFixed(2)}`, {
            x: 480,
            y,
            size: 10,
            font,
          });

          // Calculate grand total
          const grandTotal = challan.items.reduce((sum, item) => {
            const quantity = Number(item?.quantity ?? 0);
            const price = Number(item?.price ?? 0);
            return sum + quantity * price;
          }, 0);

          // Value
          drawSafeText(`${grandTotal.toFixed(2)}`, {
            x: 500,
            y: 150,
            size: 11,
            font,
            color: rgb(0, 0, 0),
          });
        });
      } else {
        drawSafeText('No items found.', {
          x: 50,
          y: startY,
          size: 11,
          font,
          color: rgb(1, 0, 0),
        });
      }

      // Total
      drawSafeText(`Total: ₹${challan.total ?? 0}`, {
        x: 400,
        y: 140,
        size: 12,
        font,
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url); // Save it to show in viewer
    } catch (err) {
      console.error('PDF View Failed:', err);
      alert('Failed to view challan PDF');
    }
  };

  useEffect(() => {
    fetchChallans();
  }, []);

  return (
    <SuperAdminLayout>
      <Container maxWidth="screen" sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">View Sales Challans</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/super-admin/schallan/create')}
          >
            + Create Challan
          </Button>
        </Box>

        <Dialog
          open={!!pdfUrl}
          onClose={() => setPdfUrl(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            View Challan PDF
            <Button
              variant="outlined"
              size="small"
              sx={{ float: 'right' }}
              onClick={() => {
                const a = document.createElement('a');
                a.href = pdfUrl;
                a.download = 'Challan.pdf';
                a.click();
              }}
            >
              Download PDF
            </Button>
          </DialogTitle>
          <DialogContent dividers>
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                title="Challan PDF"
                width="100%"
                height="600px"
                style={{ border: 'none' }}
              />
            )}
          </DialogContent>
        </Dialog>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Actions</TableCell>
              <TableCell>Challan No</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {challans.map((challan) => (
              <TableRow key={challan._id}>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => generatePDF(challan)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(challan._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
                <TableCell>{challan.challanNumber}</TableCell>
                <TableCell>{challan.customer}</TableCell>
                <TableCell>
                  {new Date(challan.date).toISOString().split('T')[0]}
                </TableCell>
                <TableCell>₹{challan.total.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Container>
    </SuperAdminLayout>
  );
};

export default ViewSChallan;
